/**
 * useWatchlist — Unified watchlist hook for all asset classes.
 * Provides toggle, check, and batch-check via the consolidated API.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

/**
 * useWatchlistCheck — check/toggle a single symbol.
 */
export const useWatchlistCheck = (symbol) => {
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol || !getToken()) return;
    let cancelled = false;
    fetch(`${API}/api/watchlist/check/${encodeURIComponent(symbol)}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setInWatchlist(!!d.in_watchlist); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol]);

  const toggle = useCallback(async (meta = {}) => {
    if (!symbol || !getToken()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/watchlist/toggle`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ symbol, ...meta }),
      });
      const data = await res.json();
      setInWatchlist(!!data.in_watchlist);
      return data;
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  return { inWatchlist, toggle, loading };
};


/**
 * useWatchlistBatch — batch-check a list of symbols.
 * Returns { results: { SYM: true/false }, toggle(sym, meta) }
 */
export const useWatchlistBatch = (symbols = []) => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const symbolsKey = symbols.join(',');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!symbols.length || !getToken()) return;
    let cancelled = false;
    fetch(`${API}/api/watchlist/check-batch`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ symbols }),
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && mountedRef.current) setResults(d.results || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbolsKey]);

  const toggle = useCallback(async (symbol, meta = {}) => {
    if (!symbol || !getToken()) return;
    try {
      const res = await fetch(`${API}/api/watchlist/toggle`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ symbol, ...meta }),
      });
      const data = await res.json();
      if (mountedRef.current) {
        setResults((prev) => ({ ...prev, [symbol]: !!data.in_watchlist }));
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  return { results, toggle, loading };
};

export default useWatchlistCheck;
